import { useMemo, useState } from 'react';
import { CalendarClock, CheckCircle2, ClipboardList, Plus, Timer } from 'lucide-react';
import { useWorkspace, authHeaders } from '../../context/WorkspaceContext';
import { GROWTH_MODULES, PLAN_LEVEL, PLAN_ORDER, PLANS } from '../../plans';
import { Alert, Badge, Button, EmptyState, FormField, MetricCard, PageHeader, SectionHeader, Skeleton, StatusBadge, UpgradeGate } from '../../components/ui';
import { useToast } from '../../components/ui/ToastProvider';
import DataTable from '../../components/ui/DataTable';
import { parseJSON } from './insights';
import './dashboard.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const STATUSES = ['draft', 'scheduled', 'active', 'complete', 'archived'];

export default function OperationsPage() {
  const { workspace, loading, reload } = useWorkspace();
  const { notify } = useToast();
  const level = PLAN_LEVEL[workspace.plan] || 1;
  const available = useMemo(() => GROWTH_MODULES.filter(module => level >= PLAN_LEVEL[module.minimum]), [level]);
  const lockedTiers = PLAN_ORDER.filter(tier => PLAN_LEVEL[tier] > level);

  const [moduleId, setModuleId] = useState(available[0]?.id || 'landing_pages');
  const [form, setForm] = useState({ title: '', status: 'draft', dueAt: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const records = workspace.records || [];
  const selectedModule = GROWTH_MODULES.find(module => module.id === moduleId) || available[0];
  const open = records.filter(record => !['complete', 'archived'].includes(record.status));
  const overdue = open.filter(record => record.due_at && new Date(record.due_at) < new Date());
  const scheduled = records.filter(record => record.status === 'scheduled');
  const completed = records.filter(record => record.status === 'complete');
  const rows = statusFilter === 'all' ? records : records.filter(record => record.status === statusFilter);

  const moduleTitle = record => {
    const details = parseJSON(record.details);
    return GROWTH_MODULES.find(module => module.id === details?.moduleId)?.title || String(record.record_type).replace(/_/g, ' ');
  };

  const submit = async event => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const response = await fetch(`${API}/workspace/records`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ moduleId, recordType: selectedModule.recordType, ...form }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not create this item.');
      setForm({ title: '', status: 'draft', dueAt: '' });
      await reload();
      notify(`${selectedModule.title} item created.`);
    } catch (requestError) {
      setError(requestError.message);
      notify(requestError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (recordId, status) => {
    const response = await fetch(`${API}/workspace/records/${recordId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status }) });
    if (response.ok) { await reload(); notify('Work item updated.'); }
    else notify('Could not update this item.', 'error');
  };

  if (loading) return <div className="dt-stack-4"><Skeleton height={80} /><Skeleton height={110} /><Skeleton height={280} /></div>;

  return (
    <div className="dt-stack-5">
      <PageHeader
        title="Operations"
        description="Run the tools included in your plan as tracked work with an owner and a due date."
        actions={<Button href="#create-work"><Plus size={15} />Create work item</Button>}
      />

      <div className="dt-metric-grid">
        <MetricCard label="Open work" icon={ClipboardList} value={open.length} footer="Not yet complete" />
        <MetricCard label="Past due" icon={Timer} value={overdue.length} footer={overdue.length ? 'Needs attention today' : 'Nothing overdue'} />
        <MetricCard label="Scheduled" icon={CalendarClock} value={scheduled.length} footer="Planned work" />
        <MetricCard label="Completed" icon={CheckCircle2} value={completed.length} footer="Finished items" />
      </div>

      <section className="dt-stack-3" aria-label="Work queue">
        <SectionHeader title="Work queue" description="Everything your team has in progress." />
        <DataTable
          rows={rows}
          searchKeys={['title']}
          searchPlaceholder="Search work items"
          initialSort={{ id: 'updated', direction: 'desc' }}
          caption="Operations work queue"
          filters={[{
            id: 'status', label: 'Filter by status', value: statusFilter, onChange: setStatusFilter,
            options: [{ value: 'all', label: 'All statuses' }, ...STATUSES.map(status => ({ value: status, label: status[0].toUpperCase() + status.slice(1) }))],
          }]}
          empty={<EmptyState
            icon={ClipboardList}
            title="Nothing queued yet"
            description="Pick a tool below and create your first work item. Each one keeps a status and a due date so nothing is forgotten."
            actions={<Button href="#create-work">Create work item</Button>}
          />}
          columns={[
            { id: 'title', header: 'Item', primary: true, render: row => row.title, sortValue: row => row.title.toLowerCase() },
            { id: 'module', header: 'Tool', render: row => <Badge tone="neutral">{moduleTitle(row)}</Badge> },
            { id: 'due', header: 'Due', render: row => row.due_at
                ? <span className={new Date(row.due_at) < new Date() && !['complete', 'archived'].includes(row.status) ? 'dt-change is-down' : ''}>{new Date(row.due_at).toLocaleDateString()}</span>
                : <span className="dt-support">No date</span>,
              sortValue: row => (row.due_at ? new Date(row.due_at).getTime() : Number.MAX_SAFE_INTEGER) },
            { id: 'status', header: 'Status', render: row => <StatusBadge status={row.status} />, sortValue: row => row.status },
            { id: 'updated', header: 'Updated', render: row => new Date(row.updated_at).toLocaleDateString(), sortValue: row => new Date(row.updated_at).getTime() },
            { id: 'actions', header: 'Change status', align: 'right', render: row => (
              <select className="dt-select" style={{ width: 130 }} value={row.status} onChange={event => updateStatus(row.id, event.target.value)} aria-label={`Status for ${row.title}`}>
                {STATUSES.map(status => <option value={status} key={status}>{status[0].toUpperCase() + status.slice(1)}</option>)}
              </select>
            ) },
          ]}
          renderCard={row => (
            <>
              <strong className="dt-card-title">{row.title}</strong>
              <dl>
                <dt>Tool</dt><dd>{moduleTitle(row)}</dd>
                <dt>Status</dt><dd><StatusBadge status={row.status} /></dd>
                <dt>Due</dt><dd>{row.due_at ? new Date(row.due_at).toLocaleDateString() : '—'}</dd>
              </dl>
              <select className="dt-select" value={row.status} onChange={event => updateStatus(row.id, event.target.value)} aria-label={`Status for ${row.title}`}>
                {STATUSES.map(status => <option value={status} key={status}>{status[0].toUpperCase() + status.slice(1)}</option>)}
              </select>
            </>
          )}
        />
      </section>

      <section className="dt-card dt-stack-4" id="create-work" aria-label="Create work item">
        <SectionHeader title="Create work item" description={`${available.length} tools are included in your ${PLANS[workspace.plan]?.name || 'Launch'} plan.`} />
        <div className="ops-modules" role="group" aria-label="Choose a tool">
          {available.map(module => (
            <button type="button" className="ops-module" key={module.id} aria-pressed={moduleId === module.id} onClick={() => setModuleId(module.id)}>
              <strong>{module.title}</strong>
              <small>{module.description}</small>
            </button>
          ))}
        </div>
        <form className="dt-stack-4" onSubmit={submit}>
          <FormField label="Title" id="work-title" required>
            <input id="work-title" className="dt-input" value={form.title} onChange={event => setForm(current => ({ ...current, title: event.target.value }))} placeholder={`${selectedModule?.title || 'New'} for spring campaign`} required />
          </FormField>
          <div className="dt-field-grid">
            <FormField label="Status" id="work-status">
              <select id="work-status" className="dt-select" value={form.status} onChange={event => setForm(current => ({ ...current, status: event.target.value }))}>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="active">Active</option>
              </select>
            </FormField>
            <FormField label="Due date" id="work-due">
              <input id="work-due" className="dt-input" type="datetime-local" value={form.dueAt} onChange={event => setForm(current => ({ ...current, dueAt: event.target.value }))} />
            </FormField>
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          <div><Button type="submit" loading={busy}><Plus size={15} />Create {selectedModule?.title || 'item'}</Button></div>
        </form>
      </section>

      {lockedTiers.length > 0 && (
        <section className="dt-stack-3" aria-label="Available on higher plans">
          <SectionHeader title="Available as you grow" description="These tools unlock automatically when you upgrade." />
          {lockedTiers.map(tier => (
            <UpgradeGate
              key={tier}
              plan={tier}
              title={`${GROWTH_MODULES.filter(module => module.minimum === tier).length} more tools with ${PLANS[tier].name}`}
              description={GROWTH_MODULES.filter(module => module.minimum === tier).map(module => module.title).join(' · ')}
            />
          ))}
        </section>
      )}
    </div>
  );
}
